import { useCallback, useEffect, useState, useRef } from 'react';
import BigButton from '../../button/BigButton';
import { toast } from 'react-toastify';
import {UpdateProductImageRequest, useProductStore} from '../../../store/useProduct';
import { useNavigate, useParams } from 'react-router-dom';
import FileUpload from '../../form/FileUpload';
import apiClient from '../../../service/apiClient';
import { formatNumberWithComma, getDiscountRate } from '../../../util/util';
import TemplateImageModal from './modal/TemplateImageModal';
import TemplateFontModal from './modal/TemplateFontModal';
import TemplateStyleModal from "./modal/TemplateStyleModal";

/**
 * @author assistant
 * @date 2025.01.04
 * @description 상품 수정 페이지
 */
const ProductUpdate = () => {
    const params = useParams();
    const navigate = useNavigate();
    const { updateProductImage, getProductDetail, productDetail, getProductPreview } = useProductStore();

    // URL에서 직접 ID 추출
    const urlParts = window.location.pathname.split('/');
    const productId = urlParts[urlParts.length - 1];

    // 폼 데이터
    const [productCode, setProductCode] = useState<string>('');
    const [productName, setProductName] = useState<string>('');
    const [price, setPrice] = useState<string>('');
    const [discountPrice, setDiscountPrice] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const [custInfoCount, setCustInfoCount] = useState<string>('1');

    // 이미지 관련
    const [newThumbnail, setNewThumbnail] = useState<{file: File; id: string} | null>(null);
    const [newInline, setNewInline] = useState<{file: File; id: string} | null>(null);
    const [newDetailImages, setNewDetailImages] = useState<{file: File; id: string}[]>([]);
    const [existingThumbnail, setExistingThumbnail] = useState<string>('');
    const [existingInline, setExistingInline] = useState<string>('');
    const [existingDetailImages, setExistingDetailImages] = useState<string[]>([]);
    const [previewHtml, setPreviewHtml] = useState<string>('');
    const [endFiles, setEndFiles] = useState<File[]>([]);
    const [newEndImage, setNewEndImage] = useState<{file: File; id: string} | null>(null);
    const [existingEnd, setExistingEnd] = useState<string>('');
    
    // 템플릿 모달 상태
    const [templateImageModalOpen, setTemplateImageModalOpen] = useState(false);
    const [templateFontModalOpen, setTemplateFontModalOpen] = useState(false);
    const [templateStyleModalOpen, setTemplateStyleModalOpen] = useState(false);

    // iframe 스크롤 위치 저장용
    const iframeRef = useRef<HTMLIFrameElement | null>(null);
    const scrollPositionRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

    // 특정 컨텐츠(wonguk_img_container)의 bottom row가 페이지 여백을 넘으면 통째로 숨기고, 잘림 표시 추가
    const enforceWongukBounds = useCallback((iframe: HTMLIFrameElement) => {
        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        if (!doc) return;

        const pageNodes = Array.from(doc.querySelectorAll('article')) as HTMLElement[];
        if (!pageNodes.length) return;

        pageNodes.forEach((page) => {
            const style = doc.defaultView?.getComputedStyle(page);
            const padTop = style ? parseFloat(style.paddingTop || '0') : 0;
            const padBottom = style ? parseFloat(style.paddingBottom || '0') : 0;
            const pageRect = page.getBoundingClientRect();
            const contentTop = pageRect.top + padTop;
            const contentBottom = pageRect.bottom - padBottom;

            const containers = Array.from(page.querySelectorAll('.wonguk_img_container')) as HTMLElement[];
            containers.forEach((container) => {
                const bottomRow = container.querySelector('.row.bottom') as HTMLElement | null;
                if (!bottomRow) return;

                const rowRect = bottomRow.getBoundingClientRect();
                const fits = rowRect.top >= contentTop && rowRect.bottom <= contentBottom;
                const indicator = container.querySelector('.wonguk_cut_indicator') as HTMLElement | null;
                if (!fits) {
                    if (bottomRow.style.display !== 'none') bottomRow.style.display = 'none';
                    if (!indicator) {
                        const ind = doc.createElement('div');
                        ind.className = 'wonguk_cut_indicator';
                        ind.textContent = '⚠️ 하단 행이 페이지 여백을 넘어 생략되었습니다. 실제 결과물에선 다음 페이지로 넘어갑니다.';
                        ind.setAttribute(
                            'style',
                            [
                                'margin-top:10px',
                                'width:100%',
                                'text-align:center',
                                'font-size:13px',
                                'font-weight:700',
                                'line-height:1.3',
                                'color:#7c2d12',
                                'background:rgba(254,243,199,0.95)',
                                'border:1px dashed #f59e0b',
                                'border-radius:6px',
                                'padding:8px 10px',
                                'box-shadow:0 2px 6px rgba(0,0,0,0.08)'
                            ].join(';')
                        );
                        container.appendChild(ind);
                    }
                } else {
                    if (bottomRow.style.display === 'none') bottomRow.style.display = '';
                    if (indicator) indicator.remove();
                }
            });
        });
    }, []);

    // 자수 이미지 컨테이너의 하단 이미지가 페이지 여백을 넘으면 숨기고, 잘림 표시 추가
    const enforceJasuBounds = useCallback((iframe: HTMLIFrameElement) => {
        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        if (!doc) return;

        const pageNodes = Array.from(doc.querySelectorAll('article')) as HTMLElement[];
        if (!pageNodes.length) return;

        pageNodes.forEach((page) => {
            const style = doc.defaultView?.getComputedStyle(page);
            const padTop = style ? parseFloat(style.paddingTop || '0') : 0;
            const padBottom = style ? parseFloat(style.paddingBottom || '0') : 0;
            const pageRect = page.getBoundingClientRect();
            const contentTop = pageRect.top + padTop;
            const contentBottom = pageRect.bottom - padBottom;

            const containers = Array.from(page.querySelectorAll('.wonguk_img_container')) as HTMLElement[];
            containers.forEach((container) => {
                // 좌측 컬럼의 하단 이미지 (일지)
                const leftCol = container.querySelector('.col.left') as HTMLElement | null;
                if (leftCol) {
                    const leftImages = Array.from(leftCol.querySelectorAll('.img_wrapper')) as HTMLElement[];
                    const leftBottomImg = leftImages[leftImages.length - 1]; // 마지막 이미지 (일지)

                    if (leftBottomImg) {
                        const imgRect = leftBottomImg.getBoundingClientRect();
                        const fits = imgRect.top >= contentTop && imgRect.bottom <= contentBottom;
                        const leftIndicator = leftCol.querySelector('.jasu_left_cut_indicator') as HTMLElement | null;

                        if (!fits) {
                            if (leftBottomImg.style.display !== 'none') leftBottomImg.style.display = 'none';
                            if (!leftIndicator) {
                                const ind = doc.createElement('div');
                                ind.className = 'jasu_left_cut_indicator';
                                ind.textContent = '⚠️ 일지 이미지가 페이지 여백을 넘어 생략되었습니다.';
                                ind.setAttribute(
                                    'style',
                                    [
                                        'margin-top:10px',
                                        'width:100%',
                                        'text-align:center',
                                        'font-size:12px',
                                        'font-weight:600',
                                        'line-height:1.2',
                                        'color:#7c2d12',
                                        'background:rgba(254,243,199,0.9)',
                                        'border:1px dashed #f59e0b',
                                        'border-radius:4px',
                                        'padding:6px 8px'
                                    ].join(';')
                                );
                                leftCol.appendChild(ind);
                            }
                        } else {
                            if (leftBottomImg.style.display === 'none') leftBottomImg.style.display = '';
                            if (leftIndicator) leftIndicator.remove();
                        }
                    }
                }

                // 우측 컬럼의 하단 이미지 (월지)
                const rightCol = container.querySelector('.col.right') as HTMLElement | null;
                if (rightCol) {
                    const rightImages = Array.from(rightCol.querySelectorAll('.img_wrapper')) as HTMLElement[];
                    const rightBottomImg = rightImages[rightImages.length - 1]; // 마지막 이미지 (월지)

                    if (rightBottomImg) {
                        const imgRect = rightBottomImg.getBoundingClientRect();
                        const fits = imgRect.top >= contentTop && imgRect.bottom <= contentBottom;
                        const rightIndicator = rightCol.querySelector('.jasu_right_cut_indicator') as HTMLElement | null;

                        if (!fits) {
                            if (rightBottomImg.style.display !== 'none') rightBottomImg.style.display = 'none';
                            if (!rightIndicator) {
                                const ind = doc.createElement('div');
                                ind.className = 'jasu_right_cut_indicator';
                                ind.textContent = '⚠️ 월지 이미지가 페이지 여백을 넘어 생략되었습니다.';
                                ind.setAttribute(
                                    'style',
                                    [
                                        'margin-top:10px',
                                        'width:100%',
                                        'text-align:center',
                                        'font-size:12px',
                                        'font-weight:600',
                                        'line-height:1.2',
                                        'color:#7c2d12',
                                        'background:rgba(254,243,199,0.9)',
                                        'border:1px dashed #f59e0b',
                                        'border-radius:4px',
                                        'padding:6px 8px'
                                    ].join(';')
                                );
                                rightCol.appendChild(ind);
                            }
                        } else {
                            if (rightBottomImg.style.display === 'none') rightBottomImg.style.display = '';
                            if (rightIndicator) rightIndicator.remove();
                        }
                    }
                }
            });
        });
    }, []);

    // 상품 정보 로드
    useEffect(() => {
        const loadData = async () => {
            if (productId) {
                // 상품 정보 로드
                await getProductDetail(productId);
            }
        };

        loadData();
    }, [productId]);

    // 상품 정보가 로드되면 폼에 데이터 설정
    useEffect(() => {
        const loadProductData = async () => {
            if (productDetail) {
                setProductCode(productDetail.productCode);
                setProductName(productDetail.productName);
                setPrice(formatNumberWithComma(productDetail.price));
                setCustInfoCount(productDetail.custInfoCount.toString());
                setDescription(productDetail.description);

                if (productDetail.discountPrice) {
                    setDiscountPrice(formatNumberWithComma(productDetail.discountPrice));
                }

                if (productDetail.thumbnailFile) {
                    setExistingThumbnail(productDetail.thumbnailFile.fileFullUrl);
                }

                if (productDetail.inlineFile) {
                    setExistingInline(productDetail.inlineFile.fileFullUrl);
                }

                if (productDetail.endFile) {
                    setExistingEnd(productDetail.endFile.fileFullUrl);
                }

                if (productDetail.detailFiles && productDetail.detailFiles.length > 0) {
                    setExistingDetailImages(productDetail.detailFiles.map(file => file.fileFullUrl));
                }

                // 미리보기 HTML 가져오기
                if (productDetail.thumbnailFile && productDetail.inlineFile) {
                    const preview = await getProductPreview(productId, productDetail.thumbnailFile.id, productDetail.inlineFile.id, productDetail.endFile?.id);
                    setPreviewHtml(preview);
                }
            }
        };

        loadProductData();
    }, [productDetail, productId, getProductPreview]);

    // 썸네일 파일 상태
    const [thumbnailFiles, setThumbnailFiles] = useState<File[]>([]);
    const [inlineFiles, setInlineFiles] = useState<File[]>([]);

    // 상세 이미지 파일 상태
    const [detailFiles, setDetailFiles] = useState<File[]>([]);

    // A4 비율 검증 함수
    const validateA4Ratio = (width: number, height: number): boolean => {
        const ratio = height / width;
        const a4Ratio = 1.414; // A4 비율 (297/210)
        const tolerance = 0.05; // 5% 오차 허용
        return Math.abs(ratio - a4Ratio) <= tolerance;
    };

    // 썸네일 업로드 처리
    useEffect(() => {
        const uploadThumbnail = async () => {
            if (thumbnailFiles.length > 0) {
                const file = thumbnailFiles[0];

                // 이미지 치수 검증
                const img = new Image();
                const reader = new FileReader();

                reader.onloadend = (e) => {
                    img.onload = async () => {
                        // A4 비율 검증
                        if (!validateA4Ratio(img.width, img.height)) {
                            toast.error('이미지가 A4 비율(210:297)이 아닙니다. A4 비율의 이미지를 업로드해주세요.');
                            setThumbnailFiles([]);
                            return;
                        }

                        const formData = new FormData();
                        formData.append('file', file);

                        try {
                            const response = await apiClient.post('/upload-file/upload', formData);
                            setNewThumbnail({ file, id: response.data.id });
                            setThumbnailFiles([]); // 업로드 후 초기화
                        } catch (error) {
                            console.error('이미지 업로드 실패:', error);
                            toast.error('이미지 업로드를 실패했습니다.');
                        }
                    };

                    img.src = e.target?.result as string;
                };

                reader.readAsDataURL(file);
            }
        };

        uploadThumbnail();
    }, [thumbnailFiles]);

    // 속지 업로드 처리
    useEffect(() => {
        const uploadInline = async () => {
            if (inlineFiles.length > 0) {
                const file = inlineFiles[0];

                // 이미지 치수 검증
                const img = new Image();
                const reader = new FileReader();

                reader.onloadend = (e) => {
                    img.onload = async () => {
                        // A4 비율 검증
                        if (!validateA4Ratio(img.width, img.height)) {
                            toast.error('이미지가 A4 비율(210:297)이 아닙니다. A4 비율의 이미지를 업로드해주세요.');
                            setInlineFiles([]);
                            return;
                        }

                        const formData = new FormData();
                        formData.append('file', file);

                        try {
                            const response = await apiClient.post('/upload-file/upload', formData);
                            setNewInline({ file, id: response.data.id });
                            setInlineFiles([]); // 업로드 후 초기화
                        } catch (error) {
                            console.error('이미지 업로드 실패:', error);
                            toast.error('이미지 업로드를 실패했습니다.');
                        }
                    };

                    img.src = e.target?.result as string;
                };

                reader.readAsDataURL(file);
            }
        };

        uploadInline();
    }, [inlineFiles]);

    // 맺음 이미지 업로드 처리
    useEffect(() => {
        const uploadEnd = async () => {
            if (endFiles.length > 0) {
                const file = endFiles[0];

                // 이미지 치수 검증
                const img = new Image();
                const reader = new FileReader();

                reader.onloadend = (e) => {
                    img.onload = async () => {
                        // A4 비율 검증
                        if (!validateA4Ratio(img.width, img.height)) {
                            toast.error('이미지가 A4 비율(210:297)이 아닙니다. A4 비율의 이미지를 업로드해주세요.');
                            setEndFiles([]);
                            return;
                        }

                        const formData = new FormData();
                        formData.append('file', file);

                        try {
                            const response = await apiClient.post('/upload-file/upload', formData);
                            const uploadedEndImage = { file, id: response.data.id };
                            setNewEndImage(uploadedEndImage);
                            setEndFiles([]); // 업로드 후 초기화
                        } catch (error) {
                            console.error('이미지 업로드 실패:', error);
                            toast.error('이미지 업로드를 실패했습니다.');
                        }
                    };

                    img.src = e.target?.result as string;
                };

                reader.readAsDataURL(file);
            }
        };

        uploadEnd();
    }, [endFiles]);

    // 상세 이미지 업로드 처리
    useEffect(() => {
        const uploadDetailImages = async () => {
            const validatedFiles: File[] = [];

            // 모든 파일의 A4 비율 검증
            for (const file of detailFiles) {
                await new Promise((resolve) => {
                    const img = new Image();
                    const reader = new FileReader();

                    reader.onloadend = (e) => {
                        img.onload = () => {
                            if (validateA4Ratio(img.width, img.height)) {
                                validatedFiles.push(file);
                            } else {
                                toast.error(`${file.name}이(가) A4 비율(210:297)이 아닙니다. A4 비율의 이미지만 업로드됩니다.`);
                            }
                            resolve(true);
                        };

                        img.src = e.target?.result as string;
                    };

                    reader.readAsDataURL(file);
                });
            }

            // 검증된 파일들을 순서대로 업로드하고 결과를 배열에 저장
            const uploadedImages: {file: File; id: string}[] = [];

            for (const file of validatedFiles) {
                const formData = new FormData();
                formData.append('file', file);

                try {
                    const response = await apiClient.post('/upload-file/upload', formData);
                    uploadedImages.push({ file, id: response.data.id });
                } catch (error) {
                    console.error('이미지 업로드 실패:', error);
                    toast.error('이미지 업로드를 실패했습니다.');
                }
            }

            // 업로드가 모두 완료된 후 한 번에 상태 업데이트
            if (uploadedImages.length > 0) {
                setNewDetailImages(prev => [...prev, ...uploadedImages]);
            }

            setDetailFiles([]); // 업로드 후 초기화
        };

        if (detailFiles.length > 0) {
            uploadDetailImages();
        }
    }, [detailFiles]);

    // 이미지 변경 시 미리보기 업데이트
    useEffect(() => {
        const updatePreview = async () => {
            // 새로운 썸네일 또는 속지 이미지가 있을 때만 미리보기 업데이트
            const thumbnailId = newThumbnail?.id || productDetail?.thumbnailFile?.id;
            const inlineId = newInline?.id || productDetail?.inlineFile?.id;
            const endId = newEndImage?.id || productDetail?.endFile?.id;

            if (thumbnailId && inlineId) {
                const preview = await getProductPreview(productId, thumbnailId, inlineId, endId);
                setPreviewHtml(preview);
            }
        };

        updatePreview();
    }, [newThumbnail, newInline, newEndImage, productId, productDetail, getProductPreview]);

    // 템플릿과 함께 미리보기 업데이트
    const updatePreviewWithTemplate = async () => {
        if (productDetail) {
            const preview = await getProductPreview(
                productId,
                newThumbnail?.id || productDetail.thumbnailFile?.id,
                newInline?.id || productDetail.inlineFile?.id,
                newEndImage?.id || productDetail.endFile?.id
            );
            setPreviewHtml(preview);
        }
    };

    // 수정 처리
    const handleSubmit = useCallback(async () => {

        const updateData: UpdateProductImageRequest = {};

        // 새로운 이미지가 업로드되었을 때만 추가
        if (newThumbnail) {
            updateData.thumbnailFileId = newThumbnail.id;
        }
        if (newInline) {
            updateData.inlineFileId = newInline.id;
        }
        if (newEndImage) {
            updateData.endFileId = newEndImage.id;
        }
        if (newDetailImages.length > 0) {
            updateData.detailFileIds = newDetailImages.map(item => item.id);
        }

        console.log('전송할 데이터:', updateData);
        console.log('newInline 값:', newInline);

        try {
            await updateProductImage(productId, updateData);
            toast.success('상품이 성공적으로 수정되었습니다!');
            navigate(`/product/${productId}`);
        } catch (error) {
            console.error('상품 수정 실패:', error);
            toast.error('상품 수정에 실패했습니다.');
        }
    }, [productId, price, discountPrice, description, custInfoCount, newThumbnail, newInline, newDetailImages, updateProductImage, navigate, newEndImage]);

    const closeTemplateImageModal = async () => {
        await updatePreviewWithTemplate()
        setTemplateImageModalOpen(false);
    };

    const closeTemplateFontModal = async () => {
        await updatePreviewWithTemplate()
        setTemplateFontModalOpen(false);
    };

    const closeTemplateStyleModal = async () => {
        await updatePreviewWithTemplate()
        setTemplateStyleModalOpen(false);
    };

    return (
        <>
            {/* 헤더 */}
            <div className="!flex !items-center !gap-3 !mb-4 !pt-4">
                <button
                    className="!flex !items-center !justify-center !w-8 !h-8 !rounded-full !bg-white !shadow-sm hover:!bg-gray-50 !transition-colors !border-0"
                    onClick={() => navigate(`/product/${productId}`)}
                    type="button"
                >
                    <svg className="!w-4 !h-4 !text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                    </svg>
                </button>
                <h1 className="!text-xl !font-bold !text-gray-900 !m-0">상품 이미지 수정</h1>
            </div>

            <div className="!grid !grid-cols-1 lg:!grid-cols-12 !gap-6">
                {/* 왼쪽: 이미지 수정 */}
                <div className="lg:!col-span-8 !space-y-4">
                    {/* 이미지 섹션 */}
                    <div className="!bg-white !rounded-lg !p-5">
                        <h2 className="!text-base !font-semibold !text-gray-900 !mb-4">상품 이미지
                            <span className="text-red-500">20MB 이하인 A4 비율(297/210)의 이미지만 등록 가능합니다.</span>
                        </h2>

                        <div className="!grid !grid-cols-1 md:!grid-cols-3 !gap-4">
                            <div className="!space-y-1">
                                <label className="!block !text-xs !font-medium !text-gray-700">대표 이미지</label>
                                {existingThumbnail && !newThumbnail ? (
                                    <div className="!relative">
                                        <div className="!rounded-lg !overflow-hidden !border-2 !border-gray-200 !bg-gray-50" style={{aspectRatio: '210/297'}}>
                                            <img
                                                src={existingThumbnail}
                                                alt="thumbnail"
                                                className="!w-full !h-full !object-contain"
                                            />
                                        </div>
                                        <div className="!absolute !top-2 !right-2">
                                            <div className="!px-2 !py-1 !bg-gray-500 !text-white !text-xl !rounded">
                                                기존 이미지
                                            </div>
                                        </div>
                                        <button
                                            className="!absolute !bottom-2 !right-2 !px-5 !py-3 !bg-blue-500 !text-white !text-xl !rounded hover:!bg-blue-600 !transition-colors !border-0"
                                            onClick={() => setExistingThumbnail('')}
                                            type="button"
                                        >
                                            변경
                                        </button>
                                    </div>
                                ) : newThumbnail ? (
                                    <div className="!relative">
                                        <div className="!rounded-lg !overflow-hidden !border-2 !border-gray-200 !bg-gray-50" style={{aspectRatio: '210/297'}}>
                                            <img
                                                src={URL.createObjectURL(newThumbnail.file)}
                                                alt="thumbnail"
                                                className="!w-full !h-full !object-contain"
                                            />
                                        </div>
                                        <button
                                            className="!absolute !top-2 !right-2 !px-2 !py-1 !bg-red-500 !text-white !text-xl !rounded hover:!bg-red-600 !transition-colors !border-0"
                                            onClick={() => setNewThumbnail(null)}
                                            type="button"
                                        >
                                            삭제
                                        </button>
                                    </div>
                                ) : (
                                    <div className="!aspect-square !border-2 !border-dashed !border-gray-300 !rounded-lg !flex !items-center !justify-center hover:!border-gray-400 !transition-colors">
                                        <FileUpload setFiles={setThumbnailFiles} accept="image/*" multiple={false} />
                                    </div>
                                )}
                            </div>

                            <div className="!space-y-1">
                                <label className="!block !text-xs !font-medium !text-gray-700">속지 이미지</label>
                                {existingInline && !newInline ? (
                                    <div className="!relative">
                                        <div className="!rounded-lg !overflow-hidden !border-2 !border-gray-200 !bg-gray-50" style={{aspectRatio: '210/297'}}>
                                            <img
                                                src={existingInline}
                                                alt="inline"
                                                className="!w-full !h-full !object-contain"
                                            />
                                        </div>
                                        <div className="!absolute !top-2 !right-2">
                                            <div className="!px-2 !py-1 !bg-gray-500 !text-white !text-xl !rounded">
                                                기존 이미지
                                            </div>
                                        </div>
                                        <button
                                            className="!absolute !bottom-2 !right-2 !px-5 !py-3 !bg-blue-500 !text-white !text-xl !rounded hover:!bg-blue-600 !transition-colors !border-0"
                                            onClick={() => setExistingInline('')}
                                            type="button"
                                        >
                                            변경
                                        </button>
                                    </div>
                                ) : newInline ? (
                                    <div className="!relative">
                                        <div className="!rounded-lg !overflow-hidden !border-2 !border-gray-200 !bg-gray-50" style={{aspectRatio: '210/297'}}>
                                            <img
                                                src={URL.createObjectURL(newInline.file)}
                                                alt="inline"
                                                className="!w-full !h-full !object-contain"
                                            />
                                        </div>
                                        <button
                                            className="!absolute !top-2 !right-2 !px-2 !py-1 !bg-red-500 !text-white !text-xl !rounded hover:!bg-red-600 !transition-colors !border-0"
                                            onClick={() => setNewInline(null)}
                                            type="button"
                                        >
                                            삭제
                                        </button>
                                    </div>
                                ) : (
                                    <div className="!aspect-square !border-2 !border-dashed !border-gray-300 !rounded-lg !flex !items-center !justify-center hover:!border-gray-400 !transition-colors">
                                        <FileUpload setFiles={setInlineFiles} accept="image/*" multiple={false} />
                                    </div>
                                )}
                            </div>
                            {/* 맺음말 이미지*/}
                            <div className="!space-y-1">
                                <label className="!block !text-xs !font-medium !text-gray-700">맺음말 이미지</label>
                                {existingEnd && !newEndImage ? (
                                    <div className="!relative">
                                        <div className="!rounded-lg !overflow-hidden !border-2 !border-gray-200 !bg-gray-50" style={{aspectRatio: '210/297'}}>
                                            <img
                                                src={existingEnd}
                                                alt="inline"
                                                className="!w-full !h-full !object-contain"
                                            />
                                        </div>
                                        <div className="!absolute !top-2 !right-2">
                                            <div className="!px-2 !py-1 !bg-gray-500 !text-white !text-xl !rounded">
                                                기존 이미지
                                            </div>
                                        </div>
                                        <button
                                            className="!absolute !bottom-2 !right-2 !px-5 !py-3 !bg-blue-500 !text-white !text-xl !rounded hover:!bg-blue-600 !transition-colors !border-0"
                                            onClick={() => setExistingEnd('')}
                                            type="button"
                                        >
                                            변경
                                        </button>
                                    </div>
                                ) : newEndImage ? (
                                    <div className="!relative">
                                        <div className="!rounded-lg !overflow-hidden !border-2 !border-gray-200 !bg-gray-50" style={{aspectRatio: '210/297'}}>
                                            <img
                                                src={URL.createObjectURL(newEndImage.file)}
                                                alt="endImage"
                                                className="!w-full !h-full !object-contain"
                                            />
                                        </div>
                                        <button
                                            className="!absolute !top-2 !right-2 !px-5 !py-3 !bg-red-500 !text-white !text-xl !rounded hover:!bg-red-600 !transition-colors !border-0"
                                            onClick={() => setNewEndImage(null)}
                                            type="button"
                                        >
                                            삭제
                                        </button>
                                    </div>
                                ) : (
                                    <div className="!border-2 !border-dashed !border-gray-300 !rounded-lg !flex !items-center !justify-center hover:!border-gray-400 !transition-colors" style={{aspectRatio: '210/297'}}>
                                        <FileUpload setFiles={setEndFiles} accept="image/*" multiple={false} />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* 템플릿 CSS 관리 섹션 */}
                    <div className="!bg-white !rounded-lg !p-5">
                        <h2 className="!text-base !font-semibold !text-gray-900 !mb-4">템플릿 디자인 요소</h2>

                        <div className="!space-y-3">
                            <div className="!flex !items-center !justify-between !p-3 !bg-gray-50 !rounded-lg">
                                <div>
                                    <h3 className="!text-sm !font-medium !text-gray-900">스타일 이미지</h3>
                                    <p className="!text-xs !text-gray-500 !mt-1">상품에 적용 되는 상세 이미지 변경</p>
                                </div>
                                <button
                                    className="!px-4 !py-2 !bg-blue-500 !text-white !text-sm !rounded-lg hover:!bg-blue-600 !transition-colors !border-0"
                                    onClick={() => {
                                        setTemplateImageModalOpen(true);
                                    }}
                                    type="button"
                                >
                                    이미지 변경
                                </button>
                            </div>
                        </div>
                        <div className="!space-y-3 !mt-3">
                            <div className="!flex !items-center !justify-between !p-3 !bg-gray-50 !rounded-lg">
                                <div>
                                    <h3 className="!text-sm !font-medium !text-gray-900">폰트</h3>
                                    <p className="!text-xs !text-gray-500 !mt-1">상품에 적용 되는 폰트 변경</p>
                                </div>
                                <button
                                    className="!px-4 !py-2 !bg-blue-500 !text-white !text-sm !rounded-lg hover:!bg-blue-600 !transition-colors !border-0"
                                    onClick={() => {
                                        setTemplateFontModalOpen(true);
                                    }}
                                    type="button"
                                >
                                    폰트 변경
                                </button>
                            </div>
                        </div>
                        <div className="!space-y-3 !mt-3">
                            <div className="!flex !items-center !justify-between !p-3 !bg-gray-50 !rounded-lg">
                                <div>
                                    <h3 className="!text-sm !font-medium !text-gray-900">테이블 색상 변경</h3>
                                    <p className="!text-xs !text-gray-500 !mt-1">원국표 등 상세 스타일 변경</p>
                                </div>
                                <button
                                    className="!px-4 !py-2 !bg-blue-500 !text-white !text-sm !rounded-lg hover:!bg-blue-600 !transition-colors !border-0"
                                    onClick={() => {
                                        setTemplateStyleModalOpen(true);
                                    }}
                                    type="button"
                                >
                                    스타일 변경
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 오른쪽: 미리보기 */}
                <div className="lg:!col-span-4">
                    <div className="!sticky !top-0">
                        <div className="!bg-white !rounded-lg !p-5">
                            <h3 className="!text-base !font-semibold !text-gray-900 !mb-4">디자인 미리보기
                                <span className="text-red-600">(디자인이 적용되었는지 확인해보세요!)</span>
                            </h3>

                            {/* 미리보기 영역 - 점선 테두리로 구분 */}
                            <div className="!border-2 !border-dashed !border-gray-300 !rounded-lg !bg-white !overflow-hidden"
                                 style={{
                                     width: '400px',
                                     height: '570px'
                                 }}>
                                <iframe
                                    ref={(iframe) => {
                                        iframeRef.current = iframe;
                                        if (iframe && previewHtml) {
                                            // 스크롤 위치 저장 이벤트 등록
                                            const saveScrollPosition = () => {
                                                const iframeWin = iframe.contentWindow;
                                                if (iframeWin) {
                                                    scrollPositionRef.current = {
                                                        x: iframeWin.scrollX || 0,
                                                        y: iframeWin.scrollY || 0
                                                    };
                                                }
                                            };

                                            iframe.onload = () => {
                                                const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
                                                const iframeWin = iframe.contentWindow;

                                                if (iframeDoc && iframeWin) {
                                                    // wonguk 및 jasu 컨텐츠가 여백을 넘으면 처리
                                                    try {
                                                        enforceWongukBounds(iframe);
                                                        enforceJasuBounds(iframe);
                                                        const imgs = Array.from(iframeDoc.images || []);
                                                        let pending = imgs.length;
                                                        if (pending === 0) {
                                                            enforceWongukBounds(iframe);
                                                            enforceJasuBounds(iframe);
                                                        } else {
                                                            imgs.forEach((img) => {
                                                                if (img.complete) {
                                                                    pending -= 1;
                                                                    if (pending === 0) {
                                                                        enforceWongukBounds(iframe);
                                                                        enforceJasuBounds(iframe);
                                                                    }
                                                                } else {
                                                                    img.addEventListener('load', () => {
                                                                        pending -= 1;
                                                                        if (pending === 0) {
                                                                            enforceWongukBounds(iframe);
                                                                            enforceJasuBounds(iframe);
                                                                        }
                                                                    }, { once: true });
                                                                    img.addEventListener('error', () => {
                                                                        pending -= 1;
                                                                        if (pending === 0) {
                                                                            enforceWongukBounds(iframe);
                                                                            enforceJasuBounds(iframe);
                                                                        }
                                                                    }, { once: true });
                                                                }
                                                            });
                                                        }
                                                    } catch {}
                                                    // 스크롤 위치 복원
                                                    if (scrollPositionRef.current.y > 0 || scrollPositionRef.current.x > 0) {
                                                        setTimeout(() => {
                                                            iframeWin.scrollTo(scrollPositionRef.current.x, scrollPositionRef.current.y);
                                                        }, 100);
                                                    }

                                                    // 스크롤 이벤트 리스너 등록
                                                    iframeWin.addEventListener('scroll', saveScrollPosition);
                                                }
                                            };
                                        }
                                    }}
                                    style={{
                                        width: '210mm', // A4 폭
                                        height: '297mm', // A4 높이
                                        transform: 'scale(0.5)', // 340px / (210mm * 3.78) ≈ 0.43
                                        transformOrigin: 'top left',
                                        border: 'none',
                                        display: 'block'
                                    }}
                                    srcDoc={previewHtml}
                                    title="상품 미리보기"
                                />
                            </div>
                        </div>

                        {/* 수정하기 버튼 */}
                        <div className="!mt-4">
                            <BigButton type="submit" text="상품 수정하기" onClick={handleSubmit} />
                        </div>
                    </div>
                </div>
            </div>
            
            {/* 템플릿 이미지 모달 */}
            <TemplateImageModal
                isOpen={templateImageModalOpen}
                onClose={() => closeTemplateImageModal()}
                title={'스타일 이미지 변경'}
                productId={productId}/>
            {/* 템플릿 폰트 모달 */}
            <TemplateFontModal
                isOpen={templateFontModalOpen}
                onClose={() => closeTemplateFontModal()}
                title={'폰트 변경'}
                productId={productId}/>
            <TemplateStyleModal
                isOpen={templateStyleModalOpen}
                onClose={() => closeTemplateStyleModal()}
                title={'스타일 변경'}
                productId={productId}
            />
        </>
    );
};

export default ProductUpdate;
